import { useCallback, useEffect } from 'react'
import { View } from 'react-native'
import { isWeb } from '@/components/WebWrapper'
import { useWindowResizeKey } from './useWindowResize'

/**
 * Hook to measure a view's position and update it on window resize (web only)
 * Automatically re-measures when window resizes with a debounced delay
 * @param viewRef - Ref to the View component to measure
 * @param onMeasure - Callback called with the center position (x, y) of the view
 * @param delay - Delay in ms before measuring after resize (default: 50)
 */
export const useMeasureOnResize = (
  viewRef: React.RefObject<View | null>,
  onMeasure: (x: number, y: number) => void,
  delay = 50
) => {
  const resizeKey = useWindowResizeKey()

  const measureAndUpdatePosition = useCallback(() => {
    if (!viewRef.current) return
    viewRef.current.measure?.((_x: number, _y: number, width: number, height: number, pageX: number, pageY: number) => {
      const x = pageX + width * 0.5
      const y = pageY + height * 0.5
      onMeasure(x, y)
    })
  }, [viewRef, onMeasure])

  // Re-measure when window resizes (web only)
  useEffect(() => {
    if (isWeb && resizeKey > 0) {
      const timeout = setTimeout(measureAndUpdatePosition, delay)
      return () => clearTimeout(timeout)
    }
  }, [resizeKey, measureAndUpdatePosition, delay])
}
